import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, HStack, useColorModeValue, Menu, MenuButton, MenuList, MenuItem, Box, Text, Icon, useDisclosure, Portal } from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const [currentLang, setCurrentLang] = useState(i18n.language);
  // Transparent white backgrounds (same in dark/light as requested)
  const transparentBg = 'rgba(255,255,255,0.6)';
  const transparentBgHover = 'rgba(255,255,255,0.85)';
  const activeTextColor = useColorModeValue('gray.900', 'white');
  const inactiveTextColor = useColorModeValue('gray.600', 'gray.300');
  const buttonBorder = useColorModeValue('rgba(0,0,0,0.12)', 'rgba(255,255,255,0.18)');

  useEffect(() => {
    // Update the current language when it changes
    setCurrentLang(i18n.language);
  }, [i18n.language]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    // localStorage is handled automatically by the language detector
  };

  // Helper function to get the base language code
  const getBaseLanguage = (lang) => lang.split('-')[0];

  // Check if a language is active
  const isLanguageActive = (lang) => {
    const baseCurrentLang = getBaseLanguage(currentLang);
    return baseCurrentLang === lang;
  };

  const languages = [
    { code: 'en', flag: '🇬🇧', label: 'English' },
    { code: 'fr', flag: '🇫🇷', label: 'Français' },
    { code: 'es', flag: '🇪🇸', label: 'Español' },
  ];

  const current = languages.find(l => isLanguageActive(l.code)) || languages[0];
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Menu isOpen={isOpen} onClose={onClose} placement="bottom-end" autoSelect={false}>
      <MenuButton
        as={Button}
        size="sm"
        bg={transparentBg}
        color={activeTextColor}
        px={2}
        minW="44px"
        borderWidth="0"
        borderRadius="md"
        _hover={{ bg: transparentBgHover }}
        transition="background-color 0.2s ease, color 0.2s ease"
        h="32px"
        lineHeight="32px"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
        aria-label="Change language"
      >
        <HStack spacing={1}>
          <Box as="span" fontSize="md" aria-label={current.label}>{current.flag}</Box>
          <Icon as={ChevronDownIcon} boxSize={4} color={inactiveTextColor} />
        </HStack>
      </MenuButton>
      <Portal>
        <MenuList
          minW="160px"
          p={1}
          borderColor={buttonBorder}
          bg={transparentBgHover}
          backdropFilter="blur(10px)"
          onMouseEnter={onOpen}
          onMouseLeave={onClose}
          zIndex={2000}
        >
          {languages.map((lang) => (
            <MenuItem
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              bg={isLanguageActive(lang.code) ? 'rgba(255,255,255,0.9)' : 'transparent'}
              color={isLanguageActive(lang.code) ? activeTextColor : inactiveTextColor}
              borderRadius="md"
            >
              <HStack spacing={3}>
                <Box as="span" fontSize="md">{lang.flag}</Box>
                <Text fontSize="sm">{lang.label}</Text>
              </HStack>
            </MenuItem>
          ))}
        </MenuList>
      </Portal>
    </Menu>
  );
};

export default LanguageSwitcher; 